





/**
 * The main namespace of the TomTom Maps SDK for Web. This is a place where you get access to all the services,
 * Leaflet's main object `L` and configuration.
 *
 * @class tomtom
 * @module The tomtom namespace
 */
define(function(require) {
    'use strict';
    var tomtom, requester, L, security, mapboxProvider, analytics;

    require('./globals');
    require('./polyfills');
    require('services/staticMapImage/staticMapImageFactory');

    tomtom = {};

    requester = require('requester/requester');

    analytics = require('requester/analytics');
    /**
     * This method sets the content of TomTom-User-Agent header. We are collecting anonymous data for
     * statistics of usage of our services in order to increase quality of our products.
     * @method setProductInfo
     * @param {String} productId Identifier (e.g. a name) of your application e.g.&nbsp;MyApplication
     * @param {String} productVersion Version of your application e.g.&nbsp;1.0.2
     */
    tomtom.setProductInfo = analytics.setProductInfo;

    /**
     * Factory function to create instances of the {{#crossLink "L.TomTomMarkersLayer"}}{{/crossLink}} class.
     *
     * @method tomTomMarkersLayer
     * @param {Object} [options] All the available options are described in the class page
     * @return {L.TomTomMarkersLayer}
     */
    tomtom.tomTomMarkersLayer = require('display/layers/markersLayer');

    /**
     * Tells the SDK to use JSONP instead of JSON (default) for the requests to TomTom Maps APIs.
     *
     * @example
     * ```javascript
     * tomtom.useJsonp(true); // all requests will be JSONP from now on
     * ```
     *
     * @method useJsonp
     * @param {Boolean} [useJsonp] `true`: JSONP will be used, `false` JSON will be used
     * @deprecated The support for JSONP will be removed soon. Please consider using the JSON implementation.
     */
    tomtom.useJsonp = requester.useJsonp;

    /**
     * Tells the SDK to use HTTP instead of HTTPS (default) for the requests to TomTom Maps APIs.
     *
     * This configuration can be decided per service call basis with the `protocol` option.
     *
     * @example
     * ```javascript
     * tomtom.useHttp(true); // all requests will be HTTP now
     * ````
     *
     * @method useHttp
     * @param {Boolean} [useHttp] `true`: HTTP will be used, `false` HTTPS will be used
     */
    tomtom.useHttp = requester.useHttp;

    L = require('display/map');

    /**
     * Gives you access to the <a href="https://leafletjs.com/reference-1.3.0.html">Leaflet's library</a>
     * used by the SDK.
     *
     * @property L
     * @type {Object}
     */
    tomtom.L = L;

    /**
     * Alias of the L property.
     *
     * @property display
     * @type {Object}
     */
    tomtom.display = L;

    //just to be backward compatible (we build car with thill)
    tomtom.Map = L.Map;

    /**
     * Factory function to create instances of the L.Map class.
     *
     * @method map
     * @return {L.Map}
     */
    tomtom.map = L.map;

    /**
     * Factory function to create instances of the TrafficIncidents class.
     *
     * @method traffic
     * @param {Object} [options] All the available options are described in the class page
     * @return {TrafficIncidents}
     */
    tomtom.traffic = require('services/trafficIncidents');

    /**
     * Factory function to create instances of the Copyright class.
     *
     * @method copyright
     * @param {Object} [options] All the available options are described in the class page
     * @return {Copyright}
     */
    tomtom.copyright = require('services/copyrights/copyright');

    /**
     * Factory function to create instances of the CopyrightCaption class.
     *
     * @method copyrightCaption
     * @param {Object} [options] All the available options are described in the class page
     * @return {CopyrightCaption}
     */
    tomtom.copyrightCaption = require('services/copyrights/copyrightCaption');

    /**
     * Factory function to create instances of the Geocode class.
     *
     * @method geocode
     * @param {Object} [options] All the available options are described in the class page
     * @return {Geocode}
     */
    tomtom.geocode = require('services/geocode/geocode');

    /**
     * Factory function to create instances of the class.
     *
     * @method structuredGeocode
     * @param {Object} [options] All the available options are described in the class page
     * @return {StructuredGeocode}
     */
    tomtom.structuredGeocode = require('services/structureGeocode/structuredGeocode');

    /**
     * Factory function to create instances of the ReverseGeocode class.
     *
     * @method reverseGeocode
     * @param {Object} [options] All the available options are described in the class page
     * @return {ReverseGeocode}
     */
    tomtom.reverseGeocode = require('services/reverseGeocode/reverseGeocode');
    /**
     * Factory function to create instances of the CrossStreetLookupclass.
     *
     * @method crossStreetLookup
     * @param {Object} [options] All the available options are described in the class page
     * @return {CrossStreetLookup}
     */
    tomtom.crossStreetLookup = require('services/reverseGeocode/crossStreetLookup');

    /**
     * Factory function to create instances of the FuzzySearch class.
     *
     * @method fuzzySearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {FuzzySearch}
     */
    tomtom.fuzzySearch = require('services/search/fuzzySearch');

    /**
     * Factory function to create instances of the PoiSearch class.
     *
     * @method poiSearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {PoiSearch}
     */
    tomtom.poiSearch = require('services/search/poiSearch');

    /**
     * Factory function to create instances of the CategorySearch class.
     *
     * @method categorySearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {CategorySearch}
     */
    tomtom.categorySearch = require('services/search/categorySearch');

    /**
     * Factory function to create instances of the GeometrySearch class.
     *
     * @method geometrySearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {GeometrySearch}
     */
    tomtom.geometrySearch = require('services/search/geometrySearch');

    /**
     * Factory function to create instances of the NearbySearch class.
     *
     * @method nearbySearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {NearbySearch}
     */
    tomtom.nearbySearch = require('services/search/nearbySearch');

    /**
     * Factory function to create instances of the LowBandwidthSearch class.
     *
     * @method lowBandwidthSearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {LowBandwidthSearch}
     */
    tomtom.lowBandwidthSearch = require('services/search/lowBandwidthSearch');

    /**
     * Factory function to create instances of the AlongRouteSearch class.
     *
     * @method alongRouteSearch
     * @param {Object} [options] All the available options are described in the class page
     * @return {AlongRouteSearch}
     */
    tomtom.alongRouteSearch = require('services/search/alongRouteSearch');

    /**
     * Factory function to create instances of the TrafficFlowSegmentData class.
     *
     * @method trafficFlowSegmentData
     * @param {Object} [options] All the available options are described in the class page
     * @return {trafficFlowSegmentData}
     */
    tomtom.trafficFlowSegmentData = require('services/trafficFlowSegmentData');

    /**
     * Factory function to create instances of the AdditionalData class.
     *
     * @method additionalData
     * @param {Object} [options] All the available options are described in the class page
     * @return {AdditionalData}
     */
    tomtom.additionalData = require('services/search/additionalData');

    // this was kept for backwards compatibility but it will be removed in the future
    tomtom.additionalDataProvider = tomtom.additionalData;

    /**
     * Factory function to create instances of the Routing class.
     *
     * @method routing
     * @param {Object} [options] All the available options are described in the class page
     * @return {Routing}
     */
    tomtom.routing = require('services/routing/routing');

    /**
     * Factory function to create instances of the Reachable Range Routing class.
     *
     * @method reachableRange
     * @param {Object} [options] All the available options are described in the class page
     * @return {ReachableRange}
     */
    tomtom.reachableRange = require('services/routing/reachableRange');

    /**
     * Factory function to create instances of the MatrixRouting class.
     *
     * @method matrixRouting
     * @param {Object} [options] All the available options are described in the class page
     * @return {MatrixRouting}
     */
    tomtom.matrixRouting = require('services/routing/matrixRouting');

    /**
     * Factory function that creates a new instance of the L.ControlPanel widget.
     *
     * @method controlPanel
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.ControlPanel}
     */
    tomtom.controlPanel = require('widgets/controls/controlPanel');

    /**
     * Factory function that creates a new instance of the L.Foldable widget.
     *
     * @method foldable
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.Foldable}
     */
    tomtom.foldable = require('widgets/controls/foldable');

    /**
     * Gives you access to the L.LanguageSelector module.
     * @property languageSelector
     * @type L.LanguageSelector
     */
    tomtom.languageSelector = require('widgets/controls/languageSelector');

    /**
     * Returns the global locale service instance used by all the leaflet maps.
     *
     * It lets you get and change the locale configuration used by the entire SDK.
     *
     * Whenever the {{#crossLink "LocaleService/setCurrentLocale:method"}}{{/crossLink}} method of
     * this instance is called, all the {{#crossLink "LocaleService"}}{{/crossLink}} instances used by
     * maps will be updated too.
     *
     * But if you use the {{#crossLink "LocaleService/setCurrentLocale:method"}}{{/crossLink}}
     * method in a {{#crossLink "LocaleService"}}{{/crossLink}} of a map instance only that
     * instance will be updated.
     *
     * @method globalLocaleService
     * @return {LocaleService}
     */
    tomtom.globalLocaleService = require('config/language/globalLocaleService');

    /**
     * Factory function that creates a new instance of the L.ResultsList widget.
     *
     * @method resultsList
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.ResultsList}
     */
    tomtom.resultsList = require('widgets/controls/resultsList');

    /**
     * Factory function that creates a new instance of the L.SearchBox widget.
     *
     * @method searchBox
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.SearchBox}
     */
    tomtom.searchBox = require('widgets/controls/searchBox');

    /**
     * Factory function that creates a new instance of the L.DistanceMeasurement widget.
     *
     * @method distanceMeasurement
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.DistanceMeasurement}
     */
    tomtom.distanceMeasurement = require('widgets/controls/distanceMeasurement/distanceMeasurement');

    /**
     * Factory function that creates a new instance of the L.Location widget.
     *
     * @method location
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.Location}
     */
    tomtom.location = require('widgets/controls/location');

    /**
     * Factory function that creates a new instance of the L.RouteInputs widget.
     *
     * @method routeInputs
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.RouteInputs}
     */
    tomtom.routeInputs = require('widgets/controls/routeInputs');

    /**
     * Factory function that creates a new instance of the L.RouteInstructions widget.
     *
     * @method routeInstructions
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.RouteInstructions}
     */
    tomtom.routeInstructions = require('widgets/controls/routeInstructions');

    /**
     * Factory function that creates a new instance of the L.MessageBox class.
     *
     * @method messageBox
     * @param {Object} [options] All the options are available in the class's page
     * @return {L.MessageBox}
     */
    tomtom.messageBox = require('display/messageBox');

    /**
     * Gives you access to the UnitFormatConverter module.
     *
     * @property unitFormatConverter
     * @type UnitFormatConverter
     */
    tomtom.unitFormatConverter = require('unitFormatConverter/unitFormatConverter');

    /**
     * Gives you access to the L.UnitSelector module.
     * @property unitSelector
     * @type L.UnitSelector
     */
    tomtom.unitSelector = require('widgets/controls/unitSelector');

    /**
     * Factory function that creates a new instance of the UnitService class.
     *
     * @method unitService
     * @param {UnitService} [parent] If is defined makes the new instance a "slave" of the given instance
     * @return {UnitService}
     */
    tomtom.unitService = require('config/unit/unitService');

    /**
     * Returns the global unit view service instance used by all the leaflet maps.
     *
     * It lets you get and change the unit view used by the entire SDK.
     *
     * Whenever the {{#crossLink "UnitService/setCurrentUnit:method"}}{{/crossLink}} method of
     * this instance is called, all the {{#crossLink "UnitService"}}{{/crossLink}} instances used by
     * maps will be updated too.
     *
     * But if you use the {{#crossLink "UnitService/setCurrentUnit:method"}}{{/crossLink}}
     * method in a {{#crossLink "UnitService"}}{{/crossLink}} of a map instance only that
     * instance will be updated.
     *
     * @method globalUnitService
     * @return {UnitService}
     */
    tomtom.globalUnitService = require('config/unit/globalUnitService');

    /**
     * Returns the global geopolitical view service instance used by all the leaflet maps.
     *
     * It lets you get and change the geopolitical view used by the entire SDK.
     *
     * Whenever the {{#crossLink "GeopolViewService/setCurrentGeopolView:method"}}{{/crossLink}} method of
     * this instance is called, all the {{#crossLink "GeopolViewService"}}{{/crossLink}} instances used by
     * maps will be updated too.
     *
     * But if you use the {{#crossLink "GeopolViewService/setCurrentGeopolView:method"}}{{/crossLink}}
     * method in a {{#crossLink "GeopolViewService"}}{{/crossLink}} of a map instance only that
     * instance will be updated.
     *
     * @method globalGeopolViewService
     * @return {GeopolViewService}
     */
    tomtom.globalGeopolViewService = require('config/geopolView/globalGeopolViewService');

    /**
     * Gives you access to the L.UnitSelector module.
     * @property unitSelector
     * @type L.UnitSelector
     */
    tomtom.geopolViewSelector = require('widgets/controls/geopolViewSelector');

    /**
     * Factory function that creates a new instance of the L.MarkersLayer class.
     *
     * @deprecated
     * @method markersView
     * @param {Object} [options] All the options are available in the class's page
     * @return {L.MarkersLayer}
     */
    tomtom.markersView = require('widgets/views/markers');

    /**
     * Factory function that creates a new instance of the L.RouteOnMap widget.
     *
     * @method routeOnMap
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.RouteOnMap}
     */
    tomtom.routeOnMap = require('widgets/views/routeOnMap');

    /**
     * Factory function that creates a new instance of the L.RouteSummary widget.
     *
     * @method routeSummary
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.RouteSummary}
     */
    tomtom.routeSummary = require('widgets/views/routeSummary');

    /**
     * Factory function that creates a new instance of the L.TomTomMinimap widget.
     *
     * @method miniMap
     * @param {Object} [options] All the options are available in the widget's page
     * @return {L.TomTomMinimap}
     */
    tomtom.miniMap = require('widgets/views/miniMap');

    security = require('security');

    /**
     * Sets a key 'config' object in the security configuration file. When the key's name is omitted,
     *  then the default key is set.
     *
     * @example
     * ```javascript
     * tomtom.key('keyValue');  // sets global key
     * tomtom.key('service/traffic', 'keyValue'); // sets key for traffic service
     * ```
     *
     * @method key
     * @param {String} name Name of the key to be set
     * @param {String} [value] Value of the key
     */
    tomtom.key = security.setKey;

    /**
     * Sets a key for the services: map tiles and copyrights. Can also be passed on the map object's initialization with
     * _key_ parameter.
     *
     * @example
     * ```javascript
     * tomtom.mapsKey('keyValue');  // sets maps key
     * ```
     *
     * @method mapsKey
     * @param {String} value Value of the maps key
     */
    tomtom.mapsKey = security.setKeyForName('services/display');

    /**
     * Sets a key for the vector tiles service. Can also be passed on the map object's initialization
     * with the _key_ parameter or using _key_ method. In cases where default key covers vectors tile service as well
     * setting specific key here is not required.
     *
     * @example
     * ```javascript
     * tomtom.vectorMapsKey('keyValue');  // sets the vector maps key
     * ```
     *
     * @method vectorMapsKey
     * @param {String} value Value of the vector maps key
     */
    tomtom.vectorMapsKey = security.setKeyForName('services/display/vector');

    /**
     * Sets a key for the traffic incidents tiles and services. Can also be passed on the map object's
     * initialization as an option of the traffic incidents layer.
     *
     * @example
     * ```javascript
     * tomtom.trafficKey('keyValue');  // sets traffic key
     * ```
     *
     * @method trafficKey
     * @param {String} value Value of the traffic key
     */
    tomtom.trafficKey = security.setKeyForName('services/traffic');

    /**
     * Sets a key for traffic flow tiles service. Can also be passed on the map object's initialization
     * as an option of the traffic flow layer.
     *
     * @example
     * ```javascript
     * tomtom.trafficFlowKey('keyValue');  // sets traffic flow key
     * ```
     *
     * @method trafficFlowKey
     * @param {String} value Value of the traffic flow key
     */
    tomtom.trafficFlowKey = security.setKeyForName('services/trafficFlow');

    /**
     * Sets a key for search, geocode and reverse geocode services.
     *
     * @example
     * ```javascript
     * tomtom.searchKey('keyValue');  // sets search key
     * ```
     *
     * @method searchKey
     * @param {String} value Value of the search key
     */
    tomtom.searchKey = security.setKeyForName('services/search');

    /**
     * Sets a key for the routing service.
     *
     * @example
     * ```javascript
     * tomtom.routingKey('keyValue');  // sets routing key
     * ```
     *
     * @method routingKey
     * @param {String} value Value of the routing key
     */
    tomtom.routingKey = security.setKeyForName('services/routing');

    /**
     * Factory function to create instances of the LocaleService class.
     *
     * @method localeService
     * @return {LocaleService}
     */
    tomtom.localeService = require('config/language/localeService');

    mapboxProvider = require('mapboxProvider');

    /**
     * It takes a mapbox global object that have to be used for render the vector maps.
     *
     * The use of this method is required if you use the version of this SDK version without Mapbox GL JS
     * embedded in it.
     *
     * The currently supported version of Mapbox GL JS is v${mapboxgl.version}.
     *
     * @example
     * ```js
     * tomtom.setMapbox(mapboxgl); //sets mapboxgl reference
     * ```
     *
     * @method setMapbox
     * @param {Object} mapboxgl Reference to mapboxgl object (global object provided by Mapbox GL JS library)
     */
    tomtom.setMapbox = mapboxProvider.setMapbox;

    /**
     * Factory function to create instances of the StaticMapImage class.
     *
     * @method staticMapImage
     * @param {Object} [options] All the available options are described in the class page
     * @return {StaticMapImage}
     */
    tomtom.staticMapImage = require('services/staticMapImage/staticMapImage');

    /*
     * It includes a method in L.Map to easily generate an URL to obtain an static image of the current viewport.
     */
    require('services/staticMapImage/staticMapImageFactory');

    return tomtom;
});



// WEBPACK FOOTER //
// ./main.js